import { useEffect, useMemo, useState } from 'react';

import { faGear } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLingui } from '@lingui/react/macro';
import { DatePickerWithRange } from '@repo/videa-components/src';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { differenceInDays, startOfDay, subDays } from 'date-fns';

import { useLocalizedFormatDate } from '@/modules/claims/freeClaims/state';

import { DiveBar } from '../../../../app/snackbar/Divebar';
import {
  type TppProviderSettings,
  useAlignerSeverityLabels,
  useGetCoreFourTPP,
  useUpdateCoreFourTPP,
} from '../../../../react-query/useCoreFourTPP';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';

import { EditCoreFourCriteriaView } from './EditCoreFourCriteriaView';
import { MetricCard } from './MetricCard';
import { ProviderPill } from './ProviderPill';
import { SetCriteriaPlaceholder } from './SetCriteriaPlaceholder';

const MIN_RANGE_DAYS = 30;

const emphasisClass = 'vai-text-base vai-font-semibold vai-leading-6 vai-text-zinc-900';

const labelBaseClass = 'vai-text-[12px] vai-leading-4 vai-text-muted-foreground';
const labelTextClass = `${labelBaseClass} vai-font-normal`;

const datePickerWrapperClass =
  'vai-flex vai-w-fit vai-max-w-full vai-items-center vai-gap-1.5 vai-shrink-0 [&>div>button]:vai-w-auto [&>div>button]:vai-min-w-[200px] [&>div>button]:vai-max-w-[280px] [&>div>button]:vai-gap-2 [&>div>button]:vai-py-2 [&>div>button]:vai-pl-3 [&>div>button]:vai-pr-2.5 [&>div>button_svg]:vai-h-4 [&>div>button_svg]:vai-w-4 [&>div>button_svg]:vai-shrink-0 [&>div>button_svg]:vai-text-muted-foreground';

type CoreFourDateRange = { from: Date; to?: Date };

function getDefaultCoreFourDateRange(): CoreFourDateRange {
  const end = startOfDay(subDays(new Date(), 1));
  return { from: subDays(end, MIN_RANGE_DAYS - 1), to: end };
}

export interface CoreFourTppModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerName?: string;
  providerId?: string | null;
}

type ModalView = 'main' | 'edit';

export const CoreFourTppModal = ({
  open,
  onOpenChange,
  providerName,
  providerId = null,
}: CoreFourTppModalProps) => {
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();
  const formatDate = useLocalizedFormatDate();
  const alignerSeverityLabels = useAlignerSeverityLabels();
  const defaultRange = useMemo(getDefaultCoreFourDateRange, []);

  const [dateRange, setDateRange] = useState<CoreFourDateRange>(defaultRange);
  const [view, setView] = useState<ModalView>('main');

  const startDateStr = dateRange.from ? dateRange.from.toISOString() : null;
  const endDateStr = dateRange.to ? dateRange.to.toISOString() : null;

  const { providerSettings, metrics, lastUpdated } = useGetCoreFourTPP({
    providerId,
    startDate: startDateStr,
    endDate: endDateStr,
    enabled: open,
  });
  const updateCoreFourTPP = useUpdateCoreFourTPP();

  const lastUpdatedDateStr = lastUpdated?.date
    ? formatDate(lastUpdated.date)
    : '';
  const lastUpdatedBy = lastUpdated?.by ?? '';

  useEffect(() => {
    if (open) {
      setDateRange(getDefaultCoreFourDateRange());
      setView('main');
    }
  }, [open]);

  const openEditView = () => {
    setView('edit');
  };

  const handleSaveSettings = (settings: TppProviderSettings) => {
    updateCoreFourTPP.mutate(
      { providerSettings: settings, providerId },
      {
        onSuccess: () => {
          trackEvent(mixpanelEvents.coreFourTppCriteriaSave, { ...settings });
          setView('main');
        },
      },
    );
  };

  const handleDateRangeChange = (
    newRange: { from?: Date; to?: Date } | undefined,
  ) => {
    if (!newRange?.from) {
      setDateRange(getDefaultCoreFourDateRange());
      return;
    }
    if (!newRange.to) {
      setDateRange({ from: newRange.from, to: undefined });
      return;
    }
    let from = newRange.from;
    const to = newRange.to;
    if (differenceInDays(to, from) < MIN_RANGE_DAYS) {
      from = subDays(to, MIN_RANGE_DAYS - 1);
    }
    setDateRange({ from, to });
  };

  const lastUpdatedProps = { lastUpdated, lastUpdatedDateStr, lastUpdatedBy };

  const mainViewContent = (
    <>
      <DialogHeader className="vai-shrink-0 vai-gap-2">
        <DialogTitle className="vai-text-left vai-text-2xl vai-font-semibold vai-leading-8 vai-text-slate-900">
          {t`Core Four TPP`}
        </DialogTitle>
        <DialogDescription className="vai-text-left vai-text-sm vai-leading-5 vai-text-muted-foreground">
          {t`Metrics calculated by treatment plans added to the PMS for eligible patients; treatments may or may not be completed yet.`}
        </DialogDescription>
      </DialogHeader>

      <div className="vai-flex vai-w-full vai-items-start vai-justify-between vai-gap-4">
        <div className="vai-flex vai-items-center vai-gap-2">
          {providerName && <ProviderPill name={providerName} />}
          <div className={datePickerWrapperClass}>
            <DatePickerWithRange
              date={dateRange}
              setDate={handleDateRangeChange}
              className="vai-shrink-0"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="vai-w-fit vai-min-w-0 vai-shrink-0 vai-whitespace-nowrap vai-rounded-full vai-text-xs vai-px-2.5 vai-py-1 vai-h-7 vai-text-muted-foreground hover:vai-text-zinc-900 hover:vai-bg-zinc-100"
              onClick={() => setDateRange(getDefaultCoreFourDateRange())}
            >
              {t`Last 30 days`}
            </Button>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="default"
          className="vai-shrink-0 vai-border-zinc-200 vai-bg-white hover:vai-bg-zinc-50"
          onClick={openEditView}
        >
          <FontAwesomeIcon
            icon={faGear}
            className="vai-h-4 vai-w-4 vai-text-muted-foreground"
          />
          {t`Edit`}
        </Button>
      </div>

      <div className="vai-flex vai-w-full vai-flex-col vai-gap-4">
        <div className="vai-flex vai-w-full vai-gap-4">
          <MetricCard
            title={t`Crowns`}
            criteria={
              <>
                {t`"Treat when DMF is greater than `}
                <span className={emphasisClass}>
                  {providerSettings.crownDmfThreshold}%
                </span>
                {t`"`}
              </>
            }
            metrics={metrics.crown}
            plannedPrefix={t`Crown planned for`}
          />
          <MetricCard
            title={t`SRP`}
            criteria={
              <>
                {t`"Treat when patient has `}
                <span className={emphasisClass}>
                  {t`Stage ${providerSettings.srpPerioStageThreshold}+ perio disease`}
                </span>
                {t`"`}
              </>
            }
            metrics={metrics.srp}
            plannedPrefix={t`SRP planned for`}
          />
        </div>
        <div className="vai-flex vai-w-full vai-gap-4">
          {providerSettings.incipientEnabled ? (
            <MetricCard
              title={t`Curodont`}
              criteria={
                <>
                  {t`"Treat when patient has `}
                  <span className={emphasisClass}>{t`enamel-only lesions`}</span>
                  {t`"`}
                </>
              }
              metrics={metrics.incipient}
              plannedPrefix={t`Curodont planned for`}
            />
          ) : (
            <SetCriteriaPlaceholder
              message={
                !providerSettings.alignersEnabled
                  ? t`Set treatment criteria for Curodont and Invisalign`
                  : t`Set treatment criteria for Curodont`
              }
              onClick={openEditView}
            />
          )}
          {providerSettings.alignersEnabled ? (
            <MetricCard
              title={t`Invisalign`}
              criteria={
                <>
                  {t`"Treat when patient has `}
                  <span className={emphasisClass}>
                    {alignerSeverityLabels[providerSettings.alignersSeverityThreshold ?? 'MODERATE']}
                  </span>
                  {t` cases of malocclusion"`}
                </>
              }
              metrics={metrics.aligners}
              plannedPrefix={t`Ortho planned for`}
            />
          ) : (
            providerSettings.incipientEnabled && (
              <SetCriteriaPlaceholder
                message={t`Set treatment criteria for Invisalign`}
                onClick={openEditView}
              />
            )
          )}
        </div>
      </div>

      <DialogFooter className="vai-shrink-0 vai-flex vai-items-center vai-justify-between vai-gap-4 vai-pt-2 -vai-mt-2">
        <div className="vai-flex vai-min-w-0 vai-flex-1 vai-items-center vai-justify-start vai-text-left">
          <p className={labelTextClass}>
            {lastUpdated
              ? t`Last Updated: ${lastUpdatedDateStr} by ${lastUpdatedBy}`
              : t`Last Updated: — by —`}
          </p>
        </div>
        <Button
          type="button"
          onClick={() => onOpenChange(false)}
          className="vai-shrink-0 vai-bg-blue-600 hover:vai-bg-blue-700 vai-text-white"
          data-testid="core-four-tpp-modal-close"
        >
          {t`Close`}
        </Button>
      </DialogFooter>
    </>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={`vai-font-inter vai-flex vai-w-full vai-max-w-[780px] vai-flex-col vai-gap-4 vai-p-6 ${view === 'edit' ? 'vai-overflow-visible' : 'vai-max-h-[85vh] vai-overflow-y-auto'}`}
          data-testid="core-four-tpp-modal"
        >
          {view === 'main' ? (
            mainViewContent
          ) : (
            <EditCoreFourCriteriaView
              initialSettings={providerSettings}
              providerName={providerName}
              onSave={handleSaveSettings}
              onCancel={() => setView('main')}
              isSaving={updateCoreFourTPP.isLoading}
              {...lastUpdatedProps}
            />
          )}
        </DialogContent>
      </Dialog>

      <DiveBar
        open={updateCoreFourTPP.isError}
        message={t`Error saving criteria. Please try again or contact support.`}
        severity="error"
        closable
        handleClose={updateCoreFourTPP.reset}
      />
    </>
  );
};
